import { AxiosResponse } from 'axios';
import { getActiveOrgId, applyOrgFilter } from '@/lib/activeOrg';
import { mapFxVendor } from '@/lib/mappers';
import { filterByIdOrShortId } from '@/lib/shortId';
import { supabase } from '@/lib/supabase';
import {
  asResponse,
  paginationRange,
  buildPaginatedResponse,
  throwIfError,
} from '@/lib/supabase-helpers';
import { ResponseWithPagination } from '@/types';
import { FxVendor } from '@/types/api/finance';

const SELECT_WITH_CATEGORY = '*, fx_categories(*)';

const financeVendorsService = {
  async getAll({
    page,
    take,
    search,
  }: {
    page: number;
    take: number;
    search?: string;
  }): Promise<AxiosResponse<ResponseWithPagination<FxVendor[]>>> {
    const [from, to] = paginationRange(page, take);
    let query = supabase
      .from('fx_vendors')
      .select(SELECT_WITH_CATEGORY, { count: 'exact' })
      .eq('is_active', true)
      .order('name', { ascending: true })
      .range(from, to);

    if (search) {
      query = query.ilike('name', `%${search}%`);
    }

    query = applyOrgFilter(query);

    const { data, error, count } = await query;
    if (error) throw error;
    return asResponse(
      buildPaginatedResponse((data ?? []).map(mapFxVendor), count ?? 0, page, take)
    );
  },

  async getById(id: string): Promise<AxiosResponse<FxVendor>> {
    let query = supabase
      .from('fx_vendors')
      .select(SELECT_WITH_CATEGORY);
    query = filterByIdOrShortId(query, id);
    const { data, error } = await query.single();

    if (error) throw error;
    return asResponse(mapFxVendor(data));
  },

  async create(payload: {
    name: string;
    displayName?: string;
    website?: string;
    logoUrl?: string;
    categoryId?: string;
    profileId?: string;
    notes?: string;
  }): Promise<AxiosResponse<FxVendor>> {
    const orgId = getActiveOrgId();
    const insert: Record<string, unknown> = {
      name: payload.name,
      ...(orgId ? { organization_id: orgId } : {}),
    };
    if (payload.displayName !== undefined) insert.display_name = payload.displayName;
    if (payload.website !== undefined) insert.website = payload.website;
    if (payload.logoUrl !== undefined) insert.logo_url = payload.logoUrl;
    if (payload.categoryId !== undefined) insert.category_id = payload.categoryId;
    if (payload.profileId !== undefined) insert.profile_id = payload.profileId;
    if (payload.notes !== undefined) insert.notes = payload.notes;

    const result = await supabase
      .from('fx_vendors')
      .insert(insert)
      .select(SELECT_WITH_CATEGORY)
      .single();

    return asResponse(mapFxVendor(throwIfError(result)));
  },

  async update(
    id: string,
    payload: {
      name?: string;
      displayName?: string;
      website?: string;
      logoUrl?: string;
      categoryId?: string | null;
      profileId?: string | null;
      notes?: string;
      isActive?: boolean;
    }
  ): Promise<AxiosResponse<FxVendor>> {
    const updates: Record<string, unknown> = {};
    if (payload.name !== undefined) updates.name = payload.name;
    if (payload.displayName !== undefined) updates.display_name = payload.displayName;
    if (payload.website !== undefined) updates.website = payload.website;
    if (payload.logoUrl !== undefined) updates.logo_url = payload.logoUrl;
    if (payload.categoryId !== undefined) updates.category_id = payload.categoryId;
    if (payload.profileId !== undefined) updates.profile_id = payload.profileId;
    if (payload.notes !== undefined) updates.notes = payload.notes;
    if (payload.isActive !== undefined) updates.is_active = payload.isActive;

    const result = await supabase
      .from('fx_vendors')
      .update(updates)
      .eq('id', id)
      .select(SELECT_WITH_CATEGORY)
      .single();

    return asResponse(mapFxVendor(throwIfError(result)));
  },

  async delete(id: string): Promise<AxiosResponse<FxVendor>> {
    const result = await supabase
      .from('fx_vendors')
      .update({ is_active: false })
      .eq('id', id)
      .select(SELECT_WITH_CATEGORY)
      .single();

    return asResponse(mapFxVendor(throwIfError(result)));
  },
};

export default financeVendorsService;
